import React, { useCallback, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { useForm } from 'react-final-form';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { useComponentTranslations } from 'js/hooks/component';
import { Autocomplete } from 'js/components/common/Fields/Autocomplete';
import { IAutocompleteOption } from 'js/components/common/Fields/Autocomplete/types';

import * as Types from './types';
import * as hooks from './hooks';
import * as helpers from './helpers';
import * as actions from '../../store/actions';
import { useGetFormFieldValue } from '../../hooks';
import { useDynamicReducerDispatch } from '../../store';
import { SearchOption } from './components/SearchOption';
import { useFieldLabel } from '../ChangeFormField/hooks';

import style from './AddressLookupField.scss';

export const AddressLookupField: React.FC<Types.IAddressLookupFieldConfig> = ({
    field,
    fieldId,
    disabled,
    className,
    classNames,
    isFieldHidden,
    ChangeFormField,
    validate
}) => {
    const formAPI = useForm();
    const isMounted = useIsMounted();
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const postCodeConfig = hooks.usePostCodeConfig();
    const getFormFieldValue = useGetFormFieldValue();
    const searchText = useRef<string>('');
    const [error, setError] = useState('');
    const [areAddressFieldsVisible, setAddressFieldsVisible] = useState(false);
    const [retrievedAddress, setRetrievedAddress] = useState<Types.IParsedRetrieveItem | null>(null);
    const [label, getLabelText] = useFieldLabel(field.label);

    const setAddressLookupError = useCallback((err: Types.IParsedPostCodeError | any) => {
        const defaultError = translate('playtech.mobile.system.form.addressLookup.serviceError.default');
        const message = translate(`playtech.mobile.system.form.addressLookup.serviceError.${err?.errorCode}`,
            err.errorMessage || defaultError
        );

        setError(message);
    }, [translate]);

    const { search, retrieve } = hooks.usePostCodeSearch();

    const hideInnerFields = useCallback(() => {
        setAddressFieldsVisible(false);

        batch(() => {
            dispatch(actions.removeHiddenField(field.name));
            Object.values(field.innerFields).forEach(innerField => dispatch(actions.addHiddenField(innerField.name)));
        });
    }, [dispatch, field]);

    const showInnerFields = useCallback(() => {
        setAddressFieldsVisible(true);

        batch(() => {
            dispatch(actions.addHiddenField(field.name));
            Object.values(field.innerFields).forEach(innerField => dispatch(actions.removeHiddenField(innerField.name)));
        });
    }, [dispatch, field]);

    const handleActionManualClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        setError('');
        searchText.current = '';

        formAPI.change(field.name, '');

        setRetrievedAddress(null);
        showInnerFields();
    }, [field, formAPI, showInnerFields]);

    const handleActionResetClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();

        setError('');
        searchText.current = '';

        formAPI.change(field.name, '');

        setRetrievedAddress(null);
        hideInnerFields();
    }, [field, formAPI, hideInnerFields]);

    const setAddressFields = useCallback((retrieveItem: Types.IParsedRetrieveItem) => {
        if (!isMounted()) {
            return;
        }

        setRetrievedAddress(retrieveItem);
        showInnerFields();

        formAPI.batch(() => {
            (Object.entries(field.innerFields) as Entries<typeof field.innerFields>)
                .forEach(([fieldName, innerField]) => formAPI.change(innerField.name, retrieveItem[fieldName]));
        });
    }, [field, formAPI, isMounted, showInnerFields]);

    const onSearchChange = useCallback(async (value: string): Promise<IAutocompleteOption[] | void> => {
        const countryCode = getFormFieldValue('countrycode');

        searchText.current = value;
        setError('');

        try {
            const searchResults = await search({
                searchText: value, countryCode
            });

            return helpers.searchResultsToOptions(searchResults);
        } catch (postCodeError: Types.IParsedPostCodeError | any) {
            isMounted() && setAddressLookupError(postCodeError);
        }
    }, [getFormFieldValue, isMounted, search, setAddressLookupError]);

    const onOptionSelect = useCallback(async (option: IAutocompleteOption): Promise<IAutocompleteOption[] | void> => {
        const countryCode = getFormFieldValue('countrycode');
        const selectedItem = option.data as Types.IParsedSearchItem | Types.IParsedRetrieveItem;

        setError('');

        if ('isRetrievedAddress' in selectedItem && selectedItem.isRetrievedAddress) {
            setAddressFields(selectedItem);

            return;
        }

        if ('type' in selectedItem && selectedItem.type === 'Address') {
            try {
                const retrieveResults = await retrieve({ id: selectedItem.id });

                if (retrieveResults.length === 1) {
                    setAddressFields(retrieveResults[0]);

                    return;
                }

                return helpers.retrieveResultsToOptions(retrieveResults);
            } catch (postCodeError: Types.IParsedPostCodeError | any) {
                setAddressLookupError(postCodeError);
            }

            return;
        }

        try {
            const searchResults = await search({
                searchText: searchText.current, id: selectedItem.id, countryCode
            });

            return helpers.searchResultsToOptions(searchResults);
        } catch (postCodeError: Types.IParsedPostCodeError | any) {
            setAddressLookupError(postCodeError);
        }
    }, [getFormFieldValue, search, retrieve, setAddressFields, setAddressLookupError]);

    const { isLabelAsteriskEnabled, isPlaceholderAsteriskEnabled } = field.validationRules?.find(rule => rule.type === 'required') || {};

    useEffectOnce(() => {
        if (!postCodeConfig.isEnabled) {
            return;
        }

        hideInnerFields();
    }, [hideInnerFields, postCodeConfig]);

    return (
        <>
            {postCodeConfig.isEnabled && !areAddressFieldsVisible && (
                <>
                    <Autocomplete
                        aria-label={getLabelText() || translate([field.placeholder, 'playtech.mobile.system.input.aria-label'])}
                        classNames={{
                            container: classNames?.autocomplete,
                            control: style.autocompleteInput
                        }}
                        disabled={disabled}
                        error={error}
                        id={fieldId}
                        infoTooltip={field.infoTooltip && translate(field.infoTooltip)}
                        isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                        isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                        label={label}
                        minSymbolsToSearch={Number(field.condition) || 1}
                        name={field.name}
                        noOptionsMessage={translate('playtech.mobile.system.form.addressLookup.serviceError.noResults')}
                        options={[]}
                        Option={SearchOption}
                        onSearchChange={onSearchChange}
                        onOptionSelect={onOptionSelect}
                        placeholder={field.placeholder && translate(field.placeholder, field.placeholder)}
                        validate={validate}
                    />
                    {!isFieldHidden && (
                        <a href="#" className={style.toggleManualInput} onClick={handleActionManualClick}>{
                            translate('playtech.mobile.system.form.addressLookup.action.manually')
                        }</a>
                    )}
                </>
            )}
            {(!postCodeConfig.isEnabled || areAddressFieldsVisible) && (
                <>
                    {(Object.entries(field.innerFields) as Entries<typeof field.innerFields>).map(([fieldType, innerField]) => (
                        <ChangeFormField
                            key={innerField.name}
                            field={innerField}
                            className={className}
                            isFieldHidden={isFieldHidden}
                            disabled={disabled || retrievedAddress?.[fieldType]}
                        />))
                    }
                    {postCodeConfig.isEnabled && !isFieldHidden && (
                        <a
                            href="#"
                            className={style.toggleManualInput}
                            onClick={retrievedAddress ? handleActionManualClick : handleActionResetClick}
                        >{retrievedAddress
                            ? translate('playtech.mobile.system.form.addressLookup.action.manually')
                            : translate('playtech.mobile.system.form.addressLookup.action.reset')
                        }</a>
                    )}
                </>
            )}
        </>
    );
};
